<template>
  <div id="app">
    <el-container class="layout-container">
      <el-header class="header" height="auto">
        <nav-bar />
      </el-header>
      <el-main class="main-content">
        <router-view />
      </el-main>
      <app-footer />
    </el-container>

    <!-- University Selection Dialog -->
    <university-selection-dialog
      v-model="showUniversityDialog"
      @confirmed="handleUniversityConfirmed"
      @skipped="handleUniversitySkipped"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from "vue";
import NavBar from "@/components/NavBar.vue";
import AppFooter from "@/components/AppFooter.vue";
import UniversitySelectionDialog from "@/components/UniversitySelectionDialog.vue";
import { useAuthStore } from "@/stores/auth";
import { useRouter, useRoute } from "vue-router";

const authStore = useAuthStore();
const router = useRouter();
const route = useRoute();
const showUniversityDialog = ref(false);

const checkUniversitySelection = () => {
  if (!authStore.isAuthenticated || !authStore.initialized || !authStore.universityInitialized) {
    showUniversityDialog.value = false;
    return;
  }

  const hasStoredUniversity =
    authStore.hasSelectedUniversity || localStorage.getItem("selectedUniversity");

  if (!hasStoredUniversity && !sessionStorage.getItem("university_selection_skipped")) {
    showUniversityDialog.value = true;
  } else {
    showUniversityDialog.value = false;
  }
};

onMounted(() => {
  const savedLocale = localStorage.getItem("locale");
  const validLocales = ["en", "de"];

  if (savedLocale && !validLocales.includes(savedLocale)) {
    console.log(`Cleaning invalid locale: ${savedLocale}`);
    localStorage.removeItem("locale");
    router.go(0);
    return;
  }

  checkUniversitySelection();
});

watch(
  [
    () => authStore.isAuthenticated,
    () => authStore.initialized,
    () => authStore.universityInitialized,
    () => authStore.selectedUniversity,
  ],
  () => {
    if (authStore.isAuthenticated) {
      checkUniversitySelection();
    } else {
      showUniversityDialog.value = false;
    }
  }
);

const handleUniversityConfirmed = (universityId: number) => {
  console.log("University confirmed:", universityId);
  showUniversityDialog.value = false;
  sessionStorage.removeItem("university_selection_skipped");
  router.go(0);
};

const handleUniversitySkipped = () => {
  console.log("University selection skipped");
  sessionStorage.setItem("university_selection_skipped", "true");
  showUniversityDialog.value = false;
};
</script>

<style scoped>
.layout-container {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.header {
  background: #ffffff;
  padding: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  position: sticky;
  top: 0;
  z-index: 1000;
  height: auto;
}

.main-content {
  padding: 20px 0 0 0;
  background-color: #f5f7fa;
  min-height: calc(100vh - 120px);
  flex: 1;
}
</style>

<style>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family:
    "Helvetica Neue", Helvetica, "PingFang SC", "Hiragino Sans GB",
    "Microsoft YaHei", Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

#app {
  height: 100vh;
}
</style>
